import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Stack,
  Text,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import useAuthAs from "@raiden/library/hooks/auth/as";
import { useApi } from "@raiden/library/hooks/useApi";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUri";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { WIZARD_SUBSCRIPTIONS_LOCAL_STORAGE_KEY_INVOICE_ID } from "..";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import InvoiceDetails from "@raiden/library/components/Invoices/InvoiceDetails";
import SWRHandler from "../../../components/SWR/SWRHandler";
import PaymentsFormPayment from "../../Payments/Pay/FormPayment";
import PaymentsCreateHandler from "../../Payments/Pay/Handler";
import StepBottomBar from "../StepBottomBar";
import StepLayout from "../StepLayout";
import WizardStepPaymentDialogPayOnSpot from "./DialogPayLater";

/**
 * @typedef {Object} Props
 * @property {(step: import("..").WizardStepAlias) => void} moveToStep
 * @property {boolean} displayPaymentError
 *
 * @param {Props} props
 */
function StepPayment({ displayPaymentError, moveToStep }) {
  const [invoiceId, setInvoiceId] = useState(
    /** @type {string | null} */ (null),
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const intl = useIntl();

  const { user } = useAuthAs();

  const { bestEnvironment } = usePreferences();

  useEffect(() => {
    const invoiceId = localStorage.getItem(
      WIZARD_SUBSCRIPTIONS_LOCAL_STORAGE_KEY_INVOICE_ID,
    );
    if (invoiceId === null) {
      moveToStep("subscriptions");
      return;
    }
    setInvoiceId(invoiceId);
  }, [moveToStep]);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Invoice").Invoice>} */
  const { swrResponse: swrResponseInvoice } = useApi(
    invoiceId &&
      generateApiUri({
        id: "@invoices.view",
        parameters: { invoiceId },
        query: {
          fields: ["invoice.services", "invoice.service.facturable"],
        },
      }),
  );

  useEffect(() => {
    if (
      swrResponseInvoice.data &&
      swrResponseInvoice.data.data.paid_amount >=
        swrResponseInvoice.data.data.amount
    ) {
      moveToStep("subscriptions");
    }
  }, [moveToStep, swrResponseInvoice.data]);

  return (
    <StepLayout
      title={intl.formatMessage({
        defaultMessage: "Récapitulatif et paiement",
      })}>
      {displayPaymentError && (
        <Box px="1rem" mt="1rem">
          <Alert status="error">
            <AlertIcon />

            <Stack spacing=".5rem">
              <AlertTitle>
                <FormattedMessage defaultMessage="Erreur de paiement" />
              </AlertTitle>

              <AlertDescription>
                <FormattedMessage defaultMessage="Une erreur s'est produite lors du paiement de vos cotisations. Veuillez réessayer." />
              </AlertDescription>
            </Stack>
          </Alert>
        </Box>
      )}

      <Box px="1rem" mt="1rem">
        <Alert status="info">
          <AlertDescription>
            <FormattedMessage defaultMessage="Habitant de Golbey, nous vous invitons à payer sur place vos cotisations afin de vérifier vos aides de la ville de Golbey." />
          </AlertDescription>
        </Alert>
      </Box>

      <Text colorScheme="gray" px="1rem" mt="1rem">
        <FormattedMessage defaultMessage="Le paiement de vos cotisations aux activités et de votre cotisation annuelle d'un montant de 12€ confirmera l'envoi de vos nouvelles informations pour validation par nos services. Vous pouvez toutefois revenir en arrière pour les modifier avant paiement si nécessaire." />
      </Text>

      <SWRHandler
        swrResponse={swrResponseInvoice}
        wrapper={(children) => <Box p="1rem">{children}</Box>}>
        {({ data: invoice }) => (
          <>
            {bestEnvironment && user && (
              <PaymentsCreateHandler
                invoices={[invoice]}
                environmentId={bestEnvironment.id}
                customerId={user.id}
                initialInvoiceIds={[invoice.id]}
                successUrl={generateFrontUri({
                  id: "@account.payments.payments",
                  query: { paymentSuccess: true },
                  includeBasePath: true,
                })}
                errorUrl={generateFrontUri({
                  id: "@account.dashboard",
                  query: { paymentError: true },
                  includeBasePath: true,
                })}>
                {({ isLoading }) => (
                  <>
                    <Stack spacing="1rem" p="1rem">
                      <Box>
                        <ApiErrorMessage />

                        <InvoiceDetails invoice={invoice} />
                      </Box>

                      <PaymentsFormPayment />
                    </Stack>

                    <WizardStepPaymentDialogPayOnSpot
                      invoiceId={invoice.id}
                      isOpen={isOpen}
                      onClose={onClose}
                    />

                    <StepBottomBar>
                      <Wrap justify="flex-end" spacing=".5rem">
                        <Button onClick={onOpen}>
                          <FormattedMessage defaultMessage="Payer sur place" />
                        </Button>

                        <Button
                          type="submit"
                          isLoading={isLoading}
                          colorScheme="brandSecondary">
                          <FormattedMessage
                            defaultMessage="Confirmer et payer {price}"
                            values={{
                              price: intl.formatNumber(
                                invoice.pending_due_amount,
                                {
                                  style: "currency",
                                  currency: "EUR",
                                },
                              ),
                            }}
                          />
                        </Button>
                      </Wrap>
                    </StepBottomBar>
                  </>
                )}
              </PaymentsCreateHandler>
            )}
          </>
        )}
      </SWRHandler>
    </StepLayout>
  );
}

export default StepPayment;
